/**
 * `GET /api/stripe/me` — current user's Stripe Connect account + payout
 * preferences + PayPal-connected flag. The single read endpoint settings
 * uses to render both the PayPal and Stripe sections of the payment-methods
 * hub.
 *
 * `stripeAccount` and `preferences` are independently nullable — a seller
 * may have neither (no rail yet), only PayPal (default), only Stripe (beta),
 * or both. `paypalConnected` is derived from `profiles.paypal_merchant_id`
 * being non-null. The route uses `apiJson` so no envelope wrapping; the
 * payload IS the success body.
 *
 * @see app/api/stripe/me/route.ts — handler
 * @see components/settings/payment-methods-settings-client.tsx — hub consumer
 * @see components/settings/stripe/StripePageClient.tsx — stripe page consumer
 * @see apps/mobile/lib/api-stripe-connect.ts — mobile consumer
 */

import { z } from "zod";
import { pinned } from "./pin";

export interface StripeMeAccount {
  stripe_account_id: string;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  details_submitted: boolean;
  country: string | null;
  default_currency: string | null;
  onboarding_completed_at: string | null;
}

export interface StripeMePreferences {
  active_provider: string;
  paypal_enabled: boolean;
  stripe_enabled: boolean;
  paypal_connected: boolean;
  stripe_connected: boolean;
  updated_at: string | null;
}

export interface StripeMeResponse {
  stripeAccount: StripeMeAccount | null;
  preferences: StripeMePreferences | null;
  paypalConnected: boolean;
}

export const StripeMeAccountSchema = pinned<StripeMeAccount>()(
  z.object({
    stripe_account_id: z.string(),
    charges_enabled: z.boolean(),
    payouts_enabled: z.boolean(),
    details_submitted: z.boolean(),
    country: z.string().nullable(),
    default_currency: z.string().nullable(),
    onboarding_completed_at: z.string().nullable(),
  }),
);

export const StripeMePreferencesSchema = pinned<StripeMePreferences>()(
  z.object({
    active_provider: z.string(),
    paypal_enabled: z.boolean(),
    stripe_enabled: z.boolean(),
    paypal_connected: z.boolean(),
    stripe_connected: z.boolean(),
    updated_at: z.string().nullable(),
  }),
);

export const StripeMeResponseSchema = pinned<StripeMeResponse>()(
  z.object({
    stripeAccount: StripeMeAccountSchema.nullable(),
    preferences: StripeMePreferencesSchema.nullable(),
    paypalConnected: z.boolean(),
  }),
);
